// Las columnas de los dos grupos de líneas.
//
// LA REGLA DE ESTA PANTALLA: ninguna columna cambia de significado según dónde
// esté. En la subtabla de la lista una misma columna es «picking» arriba y
// «servido» abajo porque no cabían las dos; aquí sobra ancho, así que cada
// magnitud tiene su columna y su nombre:
//
//   pendientes → PICKING y POR LANZAR
//   facturadas → QTY SERVIDA, QTY FACTURADA y PENDIENTE DE FACTURAR
//
// La única columna que sale en los DOS grupos es ALBARÁN, y ahí la regla se
// sostiene igual: no cambia de significado, porque enseña siempre «el albarán que
// le toca a esta fila». Lo que cambia es qué fila es. Ver `albaranesLinea.ts`.
//
// Y una línea de venta puede salir en los dos grupos: la vista de origen
// devuelve una fila por (línea × factura) más otra con el remanente. Es el
// comportamiento real y no se fusiona.
import { PickingBar } from '@/components/common/PickingBar'
import type { ResumenLinea } from '@/utils/cargasPicking'
import type { ResumenAlbaranes } from '@/utils/albaranesLinea'
import type { PedidoVentaLinea } from '@/types'
import { fmtDate, fmtEur, fmtN, fmtPct, fmtQty } from '../formato'
import { apagadoCls, tenueCls } from './estilos'
import {
  celdaImporte, celdaPrecio, columnasCola, columnasIdentidad,
  type ColumnaLinea, type ContextoCeldas,
} from './celdasLinea'

/** Los albaranes los necesitan los DOS grupos, así que van en su propio contexto
 *  y no en el de pendientes. */
interface ContextoAlbaranes extends ContextoCeldas {
  albaranesDe: (l: PedidoVentaLinea) => ResumenAlbaranes | undefined
}

interface ContextoPendientes extends ContextoAlbaranes {
  pickingPorLinea: Map<string, ResumenLinea>
  lanzamientoDe: (l: PedidoVentaLinea) => { lanzado: number; pendiente: number; exceso: number }
}

/** La columna de albaranes. Misma clave y mismo rótulo en los dos grupos —hay un
 *  test que lo exige— y misma regla de fila, que vive en `albaranesLinea.ts`.
 *
 *  Se pinta como la de cargas: el primero y un «+N», la lista entera en el
 *  tooltip. Apilar una etiqueta por albarán dejó una vez una fila en 190 px de
 *  alto y volvió inmanejable una tabla de 52 líneas; y aquí lo que importa es en
 *  cuántos salió, no cuáles.
 *
 *  El vacío se explica a propósito: «sin albarán» en una fila remanente significa
 *  que esa parte no se ha servido, no que el dato falte. Ese hueco sin explicar es
 *  un ticket. */
function columnaAlbaranes(ctx: ContextoAlbaranes): ColumnaLinea {
  const { t, dark, albaranesDe } = ctx
  return {
    key: 'albaranes',
    etiqueta: t('salesOrders.line.packingSlip'),
    ancho: 'w-[150px]',
    // Ordena por el primer albarán. Son correlativos por fecha en origen, así que
    // ordenar por él ordena de hecho por antigüedad de entrega.
    valorOrden: l => albaranesDe(l)?.albaranes[0] ?? '',
    // SIN `agg`: es una lista de documentos, no una magnitud que se sume.
    render: l => {
      const r = albaranesDe(l)
      if (!r || r.albaranes.length === 0) {
        return (
          <span className={tenueCls(dark)} title={t('salesOrders.line.packingSlipEmpty')}>
            —
          </span>
        )
      }
      const titulo = [
        r.albaranes.join('\n'),
        t('salesOrders.line.packingSlipTooltip', {
          qty: fmtQty(r.qty),
          fecha: r.fechaAlbaran ? fmtDate(r.fechaAlbaran) : '—',
        }),
      ].join('\n\n')
      return (
        <span className="flex items-baseline gap-1.5 whitespace-nowrap" title={titulo}>
          <span className={`font-mono text-[10px] ${dark ? 'text-slate-300' : 'text-slate-600'}`}>
            {r.albaranes[0]}
          </span>
          {r.albaranes.length > 1 && (
            <span className={`text-[10px] font-semibold tabular-nums ${tenueCls(dark)}`}>
              +{r.albaranes.length - 1}
            </span>
          )}
        </span>
      )
    },
  }
}

/** Grupo PENDIENTE: lo que aún no se ha facturado.
 *
 *  Aquí la pregunta es logística —¿está preparado? ¿queda algo por mandar
 *  preparar?—, así que mandan picking, por lanzar y en qué cargas va. */
export function columnasPendientes(ctx: ContextoPendientes): ColumnaLinea[] {
  const { t, dark, pickingPorLinea, lanzamientoDe } = ctx
  const picking = (l: PedidoVentaLinea) =>
    l.inventtransid ? pickingPorLinea.get(l.inventtransid) : undefined

  return [
    ...columnasIdentidad(ctx),
    {
      key: 'qtyLinea',
      etiqueta: t('salesOrders.line.qtyOriginal'),
      ancho: 'w-[92px]',
      alinear: 'right',
      valorOrden: l => l.qtyLinea,
      // Cantidad de la LÍNEA. Sumable aquí porque este grupo trae como mucho una
      // fila por línea; en el grupo facturado NO lo sería.
      agg: { key: 'qtyLinea', valueKey: 'qtyLinea' },
      formatoTotal: fmtQty,
      render: l => (
        <span className={apagadoCls(dark)} title={t('salesOrders.line.qtyLineTooltip')}>
          {fmtN(l.qtyLinea)}
        </span>
      ),
    },
    {
      key: 'qtyPedida',
      etiqueta: t('salesOrders.line.qtyOrdered'),
      ancho: 'w-[92px]',
      alinear: 'right',
      valorOrden: l => l.qtyPedida,
      agg: { key: 'qtyPedida', valueKey: 'qtyPedida' },
      formatoTotal: fmtQty,
      render: l => (
        <span title={t('salesOrders.line.qtyOrderedTooltip')}>{fmtN(l.qtyPedida)}</span>
      ),
    },
    {
      key: 'picking',
      etiqueta: t('salesOrders.line.progressPickingCol'),
      ancho: 'w-[120px]',
      valorOrden: l => picking(l)?.pct ?? -1,
      render: l => {
        const p = picking(l)
        if (!p) return <span className={tenueCls(dark)}>—</span>
        const titulo = t('salesOrders.picking.lineaTooltip', {
          picking: fmtQty(p.picking),
          solicitada: fmtQty(p.solicitada),
          pendiente: fmtQty(p.pendiente),
        })
        // Mismo criterio que en las filas de carga: lo terminado no necesita
        // barra. En un pedido donde casi todo lo lanzado está picado, cincuenta
        // barras verdes tapan justo las pocas que van a medias.
        if (p.pct >= 100) {
          return (
            <span
              className={`flex items-center gap-1 text-[11px] font-semibold tabular-nums ${apagadoCls(dark)}`}
              title={titulo}
            >
              <svg className="w-3 h-3 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8.5l3.5 3.5L13 5" />
              </svg>
              100%
            </span>
          )
        }
        return <PickingBar pct={p.pct} dark={dark} compact title={titulo} />
      },
    },
    {
      key: 'qtyPicking',
      etiqueta: t('salesOrders.detalle.qtyPicking'),
      ancho: 'w-[92px]',
      alinear: 'right',
      valorOrden: l => picking(l)?.picking ?? 0,
      agg: { key: 'qtyPicking', value: l => picking(l)?.picking ?? 0 },
      formatoTotal: fmtQty,
      render: l => {
        const p = picking(l)
        return p ? fmtQty(p.picking) : <span className={tenueCls(dark)}>—</span>
      },
    },
    {
      key: 'porLanzar',
      etiqueta: t('salesOrders.line.qtyPendingLaunch'),
      ancho: 'w-[100px]',
      alinear: 'right',
      valorOrden: l => lanzamientoDe(l).pendiente,
      agg: { key: 'porLanzar', value: l => lanzamientoDe(l).pendiente },
      formatoTotal: fmtQty,
      render: l => {
        const { lanzado, pendiente, exceso } = lanzamientoDe(l)
        return (
          <span
            className="font-semibold"
            title={[
              t('salesOrders.line.pendingLaunchTooltip', {
                lanzado: fmtQty(lanzado), pedida: fmtQty(l.qtyLinea),
              }),
              exceso ? t('salesOrders.line.pendingLaunchExcess', { exceso: fmtQty(exceso) }) : '',
            ].filter(Boolean).join('\n')}
          >
            {fmtQty(pendiente)}
          </span>
        )
      },
    },
    {
      key: 'cargas',
      etiqueta: t('salesOrders.detalle.cargasCol'),
      ancho: 'w-[160px]',
      valorOrden: l => picking(l)?.cargas.length ?? 0,
      render: l => {
        const cargas = picking(l)?.cargas ?? []
        if (cargas.length === 0) return <span className={tenueCls(dark)}>—</span>
        // Una sola línea SIEMPRE. Apilando una etiqueta por carga, un artículo
        // repartido en nueve dejaba la fila en 190 px de alto y la tabla de 52
        // líneas se volvía inmanejable. El resto va en el tooltip, que es donde
        // cabe: aquí lo que importa es en cuántas va, no cuáles.
        return (
          <span className="flex items-baseline gap-1.5 whitespace-nowrap" title={cargas.join('\n')}>
            <span className={`font-mono text-[10px] ${dark ? 'text-slate-300' : 'text-slate-600'}`}>
              {cargas[0]}
            </span>
            {cargas.length > 1 && (
              <span className={`text-[10px] font-semibold tabular-nums ${tenueCls(dark)}`}>
                +{cargas.length - 1}
              </span>
            )}
          </span>
        )
      },
    },
    columnaAlbaranes(ctx),
    {
      key: 'precioUnitario',
      etiqueta: t('salesOrders.line.unitPrice'),
      ancho: 'w-[96px]',
      alinear: 'right',
      valorOrden: l => l.precioUnitario,
      // Sin total a propósito: sumar precios de artículos distintos no significa
      // nada. Mismo criterio que el pie de la subtabla de la lista.
      render: celdaPrecio,
    },
    {
      key: 'descuento',
      etiqueta: t('salesOrders.line.discount'),
      ancho: 'w-[70px]',
      alinear: 'right',
      valorOrden: l => l.descuento,
      render: l => (l.descuento ? fmtPct(l.descuento) : '—'),
    },
    {
      key: 'importeNeto',
      etiqueta: t('salesOrders.line.netAmount'),
      ancho: 'w-[112px]',
      alinear: 'right',
      valorOrden: l => l.importeNeto,
      agg: { key: 'importeNeto', valueKey: 'importeNeto', currencyKey: 'moneda', decimals: 2 },
      formatoTotal: fmtEur,
      render: l => (l.importeNeto ? fmtEur(l.importeNeto) : '—'),
    },
    {
      key: 'importePendiente',
      etiqueta: t('salesOrders.line.pendingAmount'),
      ancho: 'w-[112px]',
      alinear: 'right',
      valorOrden: l => l.importePendiente,
      agg: {
        key: 'importePendiente', valueKey: 'importePendiente',
        currencyKey: 'moneda', decimals: 2,
      },
      formatoTotal: fmtEur,
      render: l => celdaImporte(l.importePendiente, l.moneda, dark),
    },
    ...columnasCola(ctx),
  ]
}

/** Grupo FACTURADO: lo que ya tiene factura.
 *
 *  La pregunta cambia y las columnas también: qué se sirvió, qué se facturó, qué
 *  falta por facturar y en qué factura salió. */
export function columnasFacturadas(ctx: ContextoAlbaranes): ColumnaLinea[] {
  const { t, dark } = ctx

  return [
    ...columnasIdentidad(ctx),
    {
      key: 'qtyPedida',
      etiqueta: t('salesOrders.line.qtyOrdered'),
      ancho: 'w-[92px]',
      alinear: 'right',
      valorOrden: l => l.qtyPedida,
      agg: { key: 'qtyPedida', valueKey: 'qtyPedida' },
      formatoTotal: fmtQty,
      render: l => (
        <span title={t('salesOrders.line.qtyOrderedTooltip')}>{fmtN(l.qtyPedida)}</span>
      ),
    },
    {
      key: 'qtyServida',
      etiqueta: t('salesOrders.line.qtyServed'),
      ancho: 'w-[92px]',
      alinear: 'right',
      valorOrden: l => l.qtyServida,
      agg: { key: 'qtyServida', valueKey: 'qtyServida' },
      formatoTotal: fmtQty,
      render: l => (
        <span className={dark ? 'text-emerald-400' : 'text-emerald-600'}>{fmtN(l.qtyServida)}</span>
      ),
    },
    {
      key: 'qtyFacturada',
      etiqueta: t('salesOrders.invoice.billedQty'),
      ancho: 'w-[100px]',
      alinear: 'right',
      valorOrden: l => l.qtyFacturada,
      agg: { key: 'qtyFacturada', valueKey: 'qtyFacturada' },
      formatoTotal: fmtQty,
      render: l => fmtQty(l.qtyFacturada ?? 0),
    },
    {
      key: 'pendienteFacturar',
      etiqueta: t('salesOrders.line.qtyPendingBilling'),
      ancho: 'w-[120px]',
      alinear: 'right',
      valorOrden: l => l.qtySinFacturar,
      // SIN `agg` A PROPÓSITO. `qtySinFacturar` es de nivel LÍNEA y se repite en
      // cada factura de esa línea: una línea con tres facturas lo contaría tres
      // veces. El pendiente de facturar del pedido está en el resumen de arriba,
      // que sale de la cabecera y no de estas filas.
      render: l => (
        <span
          className="font-semibold"
          title={t('salesOrders.line.pendingBillingTooltip', {
            pendiente: fmtQty(l.qtySinFacturar),
            original: fmtQty(l.qtyLinea),
          })}
        >
          {fmtQty(l.qtySinFacturar)}
        </span>
      ),
    },
    {
      key: 'factura',
      etiqueta: t('salesOrders.invoice.invoice'),
      ancho: 'w-[130px]',
      valorOrden: l => l.factura,
      render: l => (
        <span className={`font-mono text-[10px] font-semibold ${dark ? 'text-slate-200' : 'text-slate-800'}`}>
          {l.factura ?? '—'}
        </span>
      ),
    },
    {
      key: 'fechaFactura',
      etiqueta: t('salesOrders.detalle.fechaFactura'),
      ancho: 'w-[100px]',
      alinear: 'right',
      valorOrden: l => l.primeraFechaFactura,
      render: l => {
        // Si la línea se facturó en varias tandas, la primera fecha sola
        // engañaría: el rango va al tooltip, que es donde cabe.
        const rango = l.ultimaFechaFactura && l.ultimaFechaFactura !== l.primeraFechaFactura
          ? `${fmtDate(l.primeraFechaFactura)} — ${fmtDate(l.ultimaFechaFactura)}`
          : undefined
        return <span title={rango}>{fmtDate(l.primeraFechaFactura)}</span>
      },
    },
    columnaAlbaranes(ctx),
    {
      key: 'importeFacturado',
      etiqueta: t('salesOrders.detalle.importeFacturado'),
      ancho: 'w-[120px]',
      alinear: 'right',
      valorOrden: l => l.importeFacturado,
      agg: {
        key: 'importeFacturado', valueKey: 'importeFacturado',
        currencyKey: 'moneda', decimals: 2,
      },
      formatoTotal: fmtEur,
      render: l => celdaImporte(l.importeFacturado, l.moneda, dark),
    },
    {
      key: 'importeNeto',
      etiqueta: t('salesOrders.line.netAmount'),
      ancho: 'w-[112px]',
      alinear: 'right',
      valorOrden: l => l.importeNeto,
      agg: { key: 'importeNeto', valueKey: 'importeNeto', currencyKey: 'moneda', decimals: 2 },
      formatoTotal: fmtEur,
      render: l => (l.importeNeto ? fmtEur(l.importeNeto) : '—'),
    },
    ...columnasCola(ctx),
  ]
}
